/**
 * MARKETING COMPASS — results (spec §3.4).
 *
 * Sections in the spec's order:
 *   Your stack → What we'd skip → In this order → Budget vs yours →
 *   What we assumed (with an edit link) → save / re-run.
 *
 * The engine runs server-side; this renders what it returns and adds no
 * judgement of its own. Prices are shown in GBP, already converted by the
 * server using the same rate src/config/currency.js displays with.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { getRecommendation, marketingErrorMessage } from './marketingApi';
import * as T from '../brand/tokens';
import {
  Loader2, AlertCircle, ExternalLink, RefreshCw, Ban, ListOrdered,
  Wallet, HelpCircle, Pencil, CheckCircle2,
} from 'lucide-react';

const gbp = (n) => `£${Math.round(Number(n) || 0).toLocaleString('en-GB')}`;

function Section({ icon: Icon, title, subtitle, children }) {
  return (
    <section className="mt-6 first:mt-0">
      <div className="mb-2 flex items-center gap-2">
        <Icon className="h-4 w-4 shrink-0 text-gold-deep" />
        <h2 className={T.H2}>{title}</h2>
      </div>
      {subtitle && <p className={"mb-2 text-sm " + T.BODY_FAINT}>{subtitle}</p>}
      {children}
    </section>
  );
}

export default function MarketingResults({ onEditAnswers, onRetake, busy = false }) {
  const [state, setState] = useState('loading');   // loading | ready | error
  const [rec, setRec] = useState(null);
  const [error, setError] = useState('');
  const [rerunning, setRerunning] = useState(false);

  const load = useCallback(async (refresh = false) => {
    try {
      const data = await getRecommendation(refresh);
      setRec(data);
      setState('ready');
    } catch (err) {
      setError(marketingErrorMessage(err));
      setState('error');
    }
  }, []);

  useEffect(() => { load(false); }, [load]);

  const rerun = async () => {
    setRerunning(true);
    await load(true);
    setRerunning(false);
  };

  if (state === 'loading') {
    return (
      <div className={"flex items-center gap-2 p-6 " + T.ON_INK}>
        <Loader2 className="h-4 w-4 animate-spin" /> Putting your stack together…
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className={T.PANEL}>
        <div className={T.PANEL_INNER + " flex items-start gap-2.5"}>
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-[#8a3a1e]" />
          <div>
            <p className="font-semibold text-[#8a3a1e]">{error}</p>
            <button type="button" className={T.BTN_SECONDARY + " mt-3"} onClick={() => load(false)}>Try again</button>
          </div>
        </div>
      </div>
    );
  }

  const { stack = [], skipped = [], sequence = [], assumptions = [], budgetTotal = {} } = rec || {};
  const nameOf = (id) => (stack.find((s) => s.productId === id) || {}).name || id;

  /**
   * The headline figure, told honestly.
   *
   * "£0/mo" on a stack of freemium tools is technically true and practically a
   * lie — it is the number you pay until the first upgrade prompt. The label
   * names the composition instead of hiding it behind a zero.
   */
  const totalLabel = (() => {
    const money = gbp(budgetTotal.combinedMonthly) + '/mo';
    const extras = budgetTotal.hasFreeTiers ? 'free tiers' : null;
    if (budgetTotal.combinedMonthly > 0) {
      return extras ? `${money} + ${extras}` : money;
    }
    if (budgetTotal.hasFreeTiers) return '£0/mo — your stack uses free tiers';
    if (budgetTotal.hasUsageBased) return '£0/mo to start';
    return money;
  })();

  return (
    <div className={T.PANEL}>
      <div className={T.PANEL_INNER}>
      <h1 className={T.H1_ON_PARCHMENT}>Your marketing stack</h1>
      <p className={"mt-1 " + T.BODY_MUTED}>
        Picked from {rec?.meta?.considered ?? 0} tools against your answers. Prices are
        indicative and shown in pounds — always check the vendor's own page before buying.
      </p>

      {/* ── Your stack ── */}
      <Section icon={CheckCircle2} title="Your stack">
        {stack.length === 0 ? (
          <div className={T.PANEL_MUTED + " border-dashed"}>
            <div className={"p-5 " + T.BODY_FAINT}>
              Nothing cleared every filter this time. Loosening a "no" answer — especially the
              budget or the genre — usually opens things up.
            </div>
          </div>
        ) : (
          <ol className="space-y-2">
            {stack.map((item, i) => {
              // Recommendations generated BEFORE the pricing-nature change have
              // no `pricing` field. Fall back to the old boolean so existing
              // stored results still render a price and a chip.
              const nature = item.pricing || (item.free ? 'free' : 'paid');
              return (
              <li key={item.productId}>
                <div className={T.PANEL_MUTED}>
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={T.MICRO + " text-gold-deep"}>{i + 1}</span>
                          <h3 className={T.H3}>{item.name}</h3>
                          {/* A bare "Free" on a freemium tool is the kind of
                              promise that meets an upgrade wall a week later.
                              Say which sort of £0 this is. */}
                          {nature === 'free' && (
                            <span className={T.CHIP + " " + T.CHIP_OK}>Free</span>
                          )}
                          {nature === 'tier' && (
                            <span className={T.CHIP + " " + T.CHIP_GOLD} title={item.pricingModel || 'Free tier, paid plans available'}>
                              Free tier
                            </span>
                          )}
                          {nature === 'usage' && (
                            <span className={T.CHIP + " " + T.CHIP_GOLD} title={item.pricingModel || 'You pay for what you use'}>
                              Pay as you use
                            </span>
                          )}
                          {item.priceVerified === false && (
                            <span className={T.CHIP + " " + T.CHIP_GOLD}>
                              Check price
                            </span>
                          )}
                        </div>
                        <p className={"mt-1 " + T.BODY_MUTED}>{item.why}</p>
                      </div>
                      <div className="shrink-0 text-right">
                        <p className={"font-cinzel font-bold " + T.BODY}>
                          {nature === 'free' ? 'Free'
                            : nature === 'tier' ? '£0 to start'
                              : nature === 'usage' ? 'You set the spend'
                                : `${gbp(item.monthlyGbp)}/mo`}
                        </p>
                        {nature === 'tier' && (
                          <p className={"mt-0.5 text-xs " + T.BODY_FAINT}>free tier</p>
                        )}
                        {item.url && (
                          <a
                            href={item.url.startsWith('http') ? item.url : `https://${item.url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-1 inline-flex items-center gap-1 text-sm font-semibold text-gold-deep hover:text-[#7c5f13]"
                          >
                            Visit <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </li>
              );
            })}
          </ol>
        )}
      </Section>

      {/* ── What we'd skip ── */}
      {skipped.length > 0 && (
        <Section
          icon={Ban}
          title="What we'd skip"
          subtitle="Popular tools that aren't right for you specifically — and why."
        >
          <ul className="space-y-2">
            {skipped.map((s) => (
              <li key={s.productId} className={T.PANEL_MUTED + " px-4 py-3"}>
                <p className={"font-semibold " + T.BODY}>{s.name}</p>
                <p className={"mt-0.5 text-sm " + T.BODY_FAINT}>{s.whyNot}</p>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* ── In this order ── */}
      {sequence.length > 0 && (
        <Section
          icon={ListOrdered}
          title="In this order"
          subtitle="Assets first, then your list, then promotion. Ads last — they amplify what already works."
        >
          <ol className="space-y-1.5">
            {sequence.map((id, i) => (
              <li key={id} className={"flex items-center gap-2.5 " + T.BODY}>
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-gold-soft bg-gold/12 text-[11px] font-semibold text-gold-deep">
                  {i + 1}
                </span>
                {nameOf(id)}
              </li>
            ))}
          </ol>
        </Section>
      )}

      {/* ── Budget ── */}
      <Section icon={Wallet} title="Budget">
        <div className={T.PANEL_MUTED + " px-4 py-3"}>
          <div className="flex items-center justify-between">
            <span className={T.BODY_MUTED}>Stack total</span>
            <span className={"font-cinzel font-bold " + T.BODY}>{totalLabel}</span>
          </div>
          {(budgetTotal.hasFreeTiers || budgetTotal.hasUsageBased) && (
            <p className={"mt-1.5 text-sm " + T.BODY_FAINT}>
              {budgetTotal.hasFreeTiers && budgetTotal.hasUsageBased
                ? 'Some of these are free tiers of tools with paid plans, and some bill for what you use — so the real figure depends on how far you push them.'
                : budgetTotal.hasFreeTiers
                  ? 'Some of these are free tiers of tools with paid plans. You can start at £0, but the paid tier is there when you outgrow it.'
                  : 'Some of these bill for what you use — ads and per-promo tools cost whatever you choose to spend.'}
            </p>
          )}
          {budgetTotal.ceiling != null && (
            <div className="mt-1.5 flex items-center justify-between">
              <span className={T.BODY_MUTED}>Your budget</span>
              <span className={T.BODY}>{gbp(budgetTotal.ceiling)}/mo</span>
            </div>
          )}
          <p className={`mt-2 text-sm ${budgetTotal.withinBudget ? 'text-[#2f5a34]' : 'text-[#8a3a1e]'}`}>
            {budgetTotal.ceiling == null
              ? "You didn't set a ceiling, so we haven't capped this — check the total works for you."
              : budgetTotal.withinBudget
                ? 'Within the budget you set.'
                : 'Over the budget you set — drop the lowest item to bring it back in.'}
          </p>
        </div>
      </Section>

      {/* ── Assumptions ── */}
      {assumptions.length > 0 && (
        <Section
          icon={HelpCircle}
          title="What we assumed"
          subtitle="Where you weren't sure, we didn't rule anything out. Here's what that means."
        >
          <ul className="space-y-2">
            {assumptions.map((a) => (
              <li key={a.questionId} className={T.PANEL_MUTED + " px-4 py-3"}>
                <p className={"font-semibold " + T.BODY}>{a.question}</p>
                <p className={"mt-0.5 text-sm " + T.BODY_FAINT}>{a.note}</p>
              </li>
            ))}
          </ul>
          {onEditAnswers && (
            <button
              onClick={onEditAnswers}
              className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-gold-deep hover:text-[#7c5f13]"
            >
              <Pencil className="h-3.5 w-3.5" /> Change your answers
            </button>
          )}
        </Section>
      )}

      {/* ── Actions ── */}
      <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row">
        {onEditAnswers && (
          <button type="button" className={T.BTN_PRIMARY} onClick={onEditAnswers} disabled={busy}>
            <Pencil className="h-3.5 w-3.5" /> Edit my answers
          </button>
        )}
        {onRetake && (
          <button type="button" className={T.BTN_SECONDARY} onClick={onRetake} disabled={busy}>
            <RefreshCw className="h-3.5 w-3.5" /> Retake the quiz
          </button>
        )}
        <button type="button" className={T.BTN_SECONDARY} onClick={rerun} disabled={rerunning || busy}>
          {rerunning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          Rebuild from these answers
        </button>
      </div>

      <p className={"mt-4 text-sm leading-relaxed " + T.BODY_FAINT}>
        <strong className="font-semibold">Edit my answers</strong> reopens this quiz with your
        answers loaded. <strong className="font-semibold">Retake the quiz</strong> starts fresh —
        this recommendation is kept in your history either way, never deleted.
      </p>
      </div>
    </div>
  );
}
